import { quitIsolatedApp } from 'src/platform/services/isolated-app-exit';

export const EMERGENCY_EXIT_LABEL = 'Exit to Home';
export const EXIT_RESPONSE_TIMEOUT_MS = 10_000;
export type EmergencyExitState = { pending: boolean; message: string; requested: boolean };

/** No stores, auth, or renderer teardown: main owns the bounded emergency shutdown. */
export function createEmergencyExitController(quit = quitIsolatedApp) {
  let state: EmergencyExitState = { pending: false, message: '', requested: false };
  let attempt = 0;
  const listeners = new Set<() => void>();
  const update = (next: EmergencyExitState) => {
    state = next;
    listeners.forEach((listener) => listener());
  };
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async request() {
      if (state.pending) return;
      attempt += 1;
      const current = attempt;
      update({ pending: true, requested: true, message: 'Closing PointCraft…' });
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          // Call immediately, without waiting for any screen's ongoing operation.
          quit(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error('Exit request timed out')),
              EXIT_RESPONSE_TIMEOUT_MS
            );
          }),
        ]);
        // Main has accepted shutdown and enforces its own eight-second deadline.
      } catch {
        if (current === attempt)
          update({
            pending: false,
            requested: true,
            message: 'Could not close PointCraft. Try Exit to Home again.',
          });
      } finally {
        clearTimeout(timer);
      }
    },
    /** Test seam; invalidates late replies without changing browser storage. */
    resetForTests() {
      attempt += 1;
      update({ pending: false, message: '', requested: false });
    },
  };
}

/** Shared across the static shell, React remounts and simultaneous controls. */
export const emergencyExit = createEmergencyExitController();

export function bindEmergencyExit(button: HTMLButtonElement, message: HTMLElement) {
  button.textContent = EMERGENCY_EXIT_LABEL;
  const render = () => {
    const state = emergencyExit.getSnapshot();
    button.disabled = state.pending;
    message.textContent = state.message;
  };
  const request = () => {
    emergencyExit.request();
  };
  button.addEventListener('click', request);
  const unsubscribe = emergencyExit.subscribe(render);
  render();
  return () => {
    button.removeEventListener('click', request);
    unsubscribe();
  };
}
